import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPECIAL_ID = "yellow-three-faces";

export default function SpecialPage() {
  const { addItem } = useCart();

  const { data: artwork, isLoading } = useQuery({
    queryKey: ["artwork", SPECIAL_ID],
    queryFn: async () => (await axios.get(`${API}/artworks/${SPECIAL_ID}`)).data,
  });

  if (isLoading || !artwork) {
    return (
      <div className="pt-32 px-6 min-h-screen flex justify-center" data-testid="special-loading">
        <div className="aspect-[4/5] w-full max-w-md bg-[#FBF3E2] animate-pulse border border-[rgba(42,33,24,0.06)]" />
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 px-6 sm:px-12 lg:px-20 min-h-screen" data-testid="special-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="text-center mb-14"
      >
        <p className="font-mono-accent text-[11px] uppercase tracking-[0.35em] text-[#C9501C] mb-4">
          One piece only
        </p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#C9501C]">
          Special
        </h1>
      </motion.div>

      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden border border-[rgba(42,33,24,0.12)] bg-[#FBF3E2]"
          data-testid="special-image-frame"
        >
          <img
            src={artwork.image_url}
            alt={artwork.title}
            className="w-full h-auto object-cover"
            data-testid="special-image"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="font-serif-display text-3xl sm:text-4xl tracking-tight text-[#C9501C]" data-testid="special-title">
            {artwork.title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-[#C9501C]" data-testid="special-description">
            {artwork.description}
          </p>
          <div className="mt-6 space-y-2 text-sm text-[#C9501C]">
            <p>{artwork.medium}</p>
            <p>{artwork.dimensions}</p>
            <p>{artwork.year}</p>
          </div>
          <p className="mt-8 font-mono-accent text-2xl font-semibold text-[#C9501C]" data-testid="special-price">
            ${artwork.price.toLocaleString()}
          </p>
          <button
            data-testid="special-add-to-cart-button"
            disabled={!artwork.is_available}
            onClick={() => {
              addItem(artwork);
              toast.success(`"${artwork.title}" added to your cart.`);
            }}
            className="mt-6 inline-flex items-center gap-3 bg-[#C9501C] hover:bg-[#d1553a] disabled:bg-[#E4D9C0] disabled:text-[#AB9C86] disabled:cursor-not-allowed text-[#FBF3E2] font-mono-accent text-xs uppercase tracking-[0.3em] px-8 py-4 transition-colors duration-300"
          >
            <ShoppingBag size={15} />
            {artwork.is_available ? "Add to cart" : "This piece has sold"}
          </button>

          <div className="mt-8">
            <Link
              to="/collections/everybody-looks"
              data-testid="special-collection-link"
              className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#C9501C] hover:text-[#2A2118] transition-colors duration-300 underline-offset-8 hover:underline"
            >
              See the Everybody Looks collection
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
